import { Loader2 } from 'lucide-react';
import { SourcingPostingRunningBatch } from 'api-clients/monolith';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ChevronRightIcon } from '@/components/icons/chevron-right';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import { useWxCopilotContext } from '@/components/pages/wx-copilot-host/context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useRunningPostingBatches } from '@/hooks/use-running-posting-batches';
import { cn } from '@/utils/classnames';

/**
 * The counts of a running bulk post as message values. Each plural is its own
 * message: Smartling cannot ingest two plural blocks in one message.
 */
export function usePostingCountValues(batch?: SourcingPostingRunningBatch) {
  const intl = useIntl();
  if (!batch) return null;
  return {
    jobs: intl.formatMessage(
      {
        defaultMessage: '{count, plural, one {# job} other {# jobs}}',
        description:
          'Job count inside the running bulk-post status, as in "Posting 20 jobs for 5 openings"',
      },
      { count: batch.total },
    ),
    openings: intl.formatMessage(
      {
        defaultMessage: '{count, plural, one {# opening} other {# openings}}',
        description:
          'Opening count inside the running bulk-post status, as in "Posting 20 jobs for 5 openings"',
      },
      { count: batch.openings_count },
    ),
    done: batch.settled,
  };
}

const spinner = (
  <Loader2
    aria-hidden="true"
    className="text-fx-brand size-3 shrink-0 animate-spin motion-reduce:animate-none"
  />
);

export const FOCUS_RING =
  'focus-visible:outline-fx-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid';

/**
 * Compact status right of the dashboard breadcrumb while a bulk post runs.
 * With several running, it shows the newest one.
 */
export function RunningPostingStatus() {
  const { postJobPath } = useNavigationPaths();
  const { batches } = useRunningPostingBatches();
  const { isCopilotRendered } = useWxCopilotContext();
  const batch = batches.at(-1);
  const values = usePostingCountValues(batch);
  if (!batch || !values) return null;

  return (
    // Clears the fixed Ask Cue launcher while it renders: 136px wide from
    // md, icon-only below it.
    <div
      className={cn(
        'flex shrink-0 items-center gap-2',
        isCopilotRendered && 'mr-14 md:mr-34',
      )}
    >
      <div role="status">
        <Tag
          className="bg-fx-brand-50"
          icon={spinner}
          text={
            <FormattedMessage
              defaultMessage="Posting {jobs} for {openings}: {done} done."
              description="Dashboard status beside the breadcrumb while a bulk job post runs in the background; {jobs} reads '20 jobs', {openings} '5 openings', {done} is how many postings have settled"
              values={values}
            />
          }
        />
      </div>
      <Button
        variant="link"
        size="link"
        className={cn('min-h-6 gap-1', FOCUS_RING)}
        asChild
      >
        <Link to={`${postJobPath}/${batch.id}`}>
          <FormattedMessage
            defaultMessage="View progress"
            description="Button opening the page that lists every posting of a running bulk job post"
          />
          <ChevronRightIcon className="text-fx-primary" />
        </Link>
      </Button>
    </div>
  );
}

/** "Posting" tag on a dashboard row whose opening is in a running bulk post. */
export function PostingTag({ batchId }: { batchId: string }) {
  const { postJobPath } = useNavigationPaths();
  return (
    <Link
      to={`${postJobPath}/${batchId}`}
      className={cn('block rounded-full', FOCUS_RING)}
    >
      <Tag
        className="bg-fx-brand-50"
        icon={spinner}
        text={
          <FormattedMessage
            defaultMessage="Posting"
            description="Tag on a dashboard opening while a bulk job post that includes it runs; opens that post's progress page"
          />
        }
      />
    </Link>
  );
}
